import { useEffect, useState } from 'react';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  TotpMultiFactorGenerator,
  TotpSecret,
  multiFactor,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  type MultiFactorResolver,
  type User,
  type UserCredential,
} from 'firebase/auth';
import { toDataURL } from 'qrcode';
import { Button, Field, TextField } from '../../components';
import { useT } from '../../lib/i18n';

/**
 * TOTP, shared between the profile card, the workspace gate and the sign-in
 * challenge — one enrolment and one challenge, so the three doors cannot
 * drift into asking differently.
 *
 * The secret never leaves the device except inside the otpauth URI rendered
 * locally into a QR: no chart service, no third party, nothing logged.
 */

export function hasTotpFactor(user: User): boolean {
  return multiFactor(user).enrolledFactors.some((f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID);
}

export function totpFactor(user: User) {
  return multiFactor(user).enrolledFactors.find((f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID) ?? null;
}

/**
 * Recent-login ceremony, inline.
 *
 * Changing second factors requires a fresh sign-in. Rather than bouncing
 * somebody out to the door, the step happens in place: a password holder
 * types it, a Google account re-opens the popup.
 */
function Reauth({ user, onDone }: { user: User; onDone: () => void }) {
  const t = useT();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasPassword = user.providerData.some((p) => p.providerId === 'password');

  const confirm = async () => {
    setError(null);
    setBusy(true);
    try {
      if (hasPassword) {
        await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email ?? '', password));
      } else {
        await reauthenticateWithPopup(user, new GoogleAuthProvider());
      }
      onDone();
    } catch {
      setError(t('twofactor.reauthFailed'));
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-pretty text-meta text-muted">{t('twofactor.reauthLede')}</p>
      {hasPassword && (
        <Field label={t('auth.password')} htmlFor="reauth-password">
          <TextField
            id="reauth-password"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
      )}
      {error && <p role="alert" className="text-meta-sm text-alert">{error}</p>}
      <Button onClick={confirm} disabled={busy || (hasPassword && !password)} className="justify-center">
        {hasPassword ? t('action.signIn') : t('auth.google')}
      </Button>
    </div>
  );
}

/**
 * Enrolment: secret, QR, six digits, done.
 */
export function TotpEnrollment({ user, onDone }: { user: User; onDone: () => void }) {
  const t = useT();
  const [secret, setSecret] = useState<TotpSecret | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [needsReauth, setNeedsReauth] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const begin = async () => {
    setError(null);
    try {
      const session = await multiFactor(user).getSession();
      const fresh = await TotpMultiFactorGenerator.generateSecret(session);
      setSecret(fresh);
      const uri = fresh.generateQrCodeUrl(user.email ?? 'account', 'Trackilero');
      setQr(await toDataURL(uri, { width: 200, margin: 1 }));
      setNeedsReauth(false);
    } catch (err) {
      const codeName = (err as { code?: string }).code ?? '';
      if (codeName === 'auth/requires-recent-login') setNeedsReauth(true);
      else if (codeName === 'auth/operation-not-allowed') setError(t('twofactor.notEnabled'));
      else setError(t('error.generic'));
    }
  };

  // The secret is minted the moment the step opens: the screen's job is the
  // QR, not a button that fetches one.
  useEffect(() => {
    void begin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enroll = async () => {
    if (!secret) return;
    setError(null);
    setBusy(true);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code.trim());
      await multiFactor(user).enroll(assertion, 'Authenticator app');
      onDone();
    } catch (err) {
      const codeName = (err as { code?: string }).code ?? '';
      setError(codeName === 'auth/invalid-verification-code' ? t('twofactor.wrongCode') : t('error.generic'));
      setBusy(false);
    }
  };

  if (needsReauth) return <Reauth user={user} onDone={() => void begin()} />;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-pretty text-meta text-muted">{t('twofactor.enrollLede')}</p>
      {qr ? (
        <div className="flex flex-col items-center gap-3">
          {/* White backing on purpose: a QR on a dark theme scans badly. */}
          <span className="rounded-panel bg-white p-2.5">
            <img src={qr} width={200} height={200} alt={t('twofactor.qrAlt')} />
          </span>
          {secret && (
            <p className="max-w-[320px] text-center text-meta-sm text-faint">
              {t('twofactor.manualKey')}{' '}
              <code className="select-all break-all font-mono text-[11px] text-muted">{secret.secretKey}</code>
            </p>
          )}
        </div>
      ) : (
        !error && <div className="h-[220px] rounded-panel bg-canvas-tint" aria-hidden />
      )}
      <Field label={t('twofactor.codeLabel')} htmlFor="totp-enroll-code">
        <TextField
          id="totp-enroll-code"
          mono
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          className="text-center tracking-[0.3em]"
        />
      </Field>
      {error && <p role="alert" className="text-meta-sm text-alert">{error}</p>}
      <Button onClick={enroll} disabled={busy || code.length !== 6 || !secret} className="justify-center">
        {t('twofactor.enable')}
      </Button>
    </div>
  );
}

/**
 * The second step of signing in, for an account that carries the factor.
 */
export function TotpChallenge({
  resolver,
  onDone,
}: {
  resolver: MultiFactorResolver;
  onDone: (credential: UserCredential) => void;
}) {
  const t = useT();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hint = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);

  const submit = async () => {
    if (!hint) return;
    setError(null);
    setBusy(true);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code.trim());
      onDone(await resolver.resolveSignIn(assertion));
    } catch {
      setError(t('twofactor.wrongCode'));
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-pretty text-meta text-muted">{t('twofactor.challengeLede')}</p>
      <Field label={t('twofactor.codeLabel')} htmlFor="totp-challenge-code">
        <TextField
          id="totp-challenge-code"
          mono
          autoFocus
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          className="text-center tracking-[0.3em]"
        />
      </Field>
      {error && <p role="alert" className="text-meta-sm text-alert">{error}</p>}
      <Button onClick={submit} disabled={busy || code.length !== 6} className="justify-center">
        {t('action.signIn')}
      </Button>
    </div>
  );
}
